import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useParams, useSearch } from '@tanstack/react-router';
import React, { useEffect, useRef, useState } from 'react';
import allViews from 'src/api/viewModels/allViews';
import DeleteDialog from 'src/components/dialogs/DeleteDialog';
import HiresDialog from 'src/components/dialogs/HiresDialog';
import LibraryDownloadDialog from 'src/components/dialogs/LibraryDownloadDialog';
import ShareDialog from 'src/components/dialogs/ShareDialog';
import {
  ADD_TO_GALLERY_SLUG,
  GALLERY_ONLY,
  GRID,
  INLINE,
  MANAGE_GALLERY_SLUG,
  REMOVE_FROM_GALLERY_SLUG,
  TWOUP,
} from 'src/constants';
import { ActionDialogContext } from 'src/context/ActionDialogContext';
import { useLibrarySearchResults } from 'src/context/LibrarySearchResultsContext';
import { buildSimpleOSQuery } from 'src/dynamoQueries/search';
import CreateGallerySwitch from 'src/Gallery/CreateGallerySwitch';
import UpdateGalleryDialog from 'src/Gallery/dialogs/UpdateGalleryDialog';
import RemoveGalleryItem from 'src/Gallery/RemoveGalleryItem';
import GalleryOnlyView from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/GalleryOnlyView/GalleryOnlyView';
import ListView from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/ListView/ListView';
import TwoUpView from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/TwoUpView/TwoUpView';
import HomePagination from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopPaginationNavBar/HomePagination/HomePagination';
import CollectionPreviewModal from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/EditCollectionDetails/CollectionPreviewModal';
import { getDensityConfig, getMediaCardBreakpoints } from 'src/helpers/densityConfig';
import { generateLibraryViewModels } from 'src/helpers/generateViewModels';
import widthToBreakPoint from 'src/helpers/widthToBreakpoint';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import viewStateVar from 'src/store/viewState';
import type { BaseSearchParams } from 'src/utils/searchSchemas';
import MediaCardHorizontalDetails from './AkomiMediaCard/MediaCardHorizontalDetails/MediaCardHorizontalDetails';
import SkeletonMediaCard from './AkomiMediaCard/SkeletonMediaCard';
import styles from './AkomiMediaCardGridStyles';
import { AkomiMediaCardRouter } from './AkomiMediaCardRouter';
import type { GalleryRouteParams, MediaCardBreakPoints } from './types';

type BreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const AkomiMediaCardGrid: React.FC = () => {
  const classes = styles();
  // Use strict: false to handle routes without galleryId param (like /library)
  const params = useParams({ strict: false }) as GalleryRouteParams;
  // Get typed search params from TanStack Router - this is the source of truth
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const { items, assetItems, searchableCount, trueTotalCount, initialLoading, refetch } = useLibrarySearchResults();

  const viewState = useReactiveVar(viewStateVar);
  const { layout, density } = useReactiveVar(akomiMediaCardGridVar);
  const densityConfig = getDensityConfig(density);

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [mediaCardBreakPoints, setMediaCardBreakPoints] = useState<MediaCardBreakPoints>(() =>
    getMediaCardBreakpoints(density, layout, undefined, false),
  );

  const [getView] = useLazyQuery(allViews, {
    variables: { viewName: 'Library' },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      generateLibraryViewModels(data);
    },
  });

  const [getGalleryViews] = useLazyQuery(allViews, {
    variables: { viewName: 'Gallery' },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      generateLibraryViewModels(data);
    },
  });

  useEffect(() => {
    const handleResize = (): void => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);

    getView();
    getGalleryViews();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getView, getGalleryViews]);

  useEffect(() => {
    if (assetItems.length && viewState.isSelectAll) {
      viewStateVar({
        ...viewStateVar(),
        selectedAssets: assetItems.map((item) => item.id),
      });
    }
  }, [assetItems, viewState.isSelectAll]);

  useEffect(() => {
    if (assetItems.length && viewState.selectedAssets.length) {
      viewStateVar({
        ...viewStateVar(),
        isSelectAll: viewState.selectedAssets.length === assetItems.length,
      });
    }
  }, [assetItems, viewState.selectedAssets]);

  useEffect(() => {
    const leftW = viewState.showLeftSidebar ? viewState.leftDrawerWidth : 0;
    const rightW = viewState.showRightSidebar ? viewState.rightDrawerWidth : 0;
    const effectiveWidth = windowWidth - leftW - rightW;
    const hasSidebar = viewState.showLeftSidebar || viewState.showRightSidebar;
    const bp = widthToBreakPoint(effectiveWidth);

    const newBreakpoints = getMediaCardBreakpoints(density, layout, bp, hasSidebar);
    setMediaCardBreakPoints(newBreakpoints);
  }, [
    density,
    layout,
    windowWidth,
    viewState.showLeftSidebar,
    viewState.showRightSidebar,
    viewState.leftDrawerWidth,
    viewState.rightDrawerWidth,
  ]);

  // Reset horizontal preview when any route/search params change (page, sort, filter, search term, gallery, etc.)
  const searchKey = JSON.stringify(search);
  const prevSearchKeyRef = useRef(searchKey);
  const prevGalleryIdRef = useRef(params.galleryId);
  useEffect(() => {
    if (prevSearchKeyRef.current !== searchKey || prevGalleryIdRef.current !== params.galleryId) {
      prevSearchKeyRef.current = searchKey;
      prevGalleryIdRef.current = params.galleryId;
      if (viewStateVar().showHorizontal) {
        viewStateVar({
          ...viewStateVar(),
          showHorizontal: false,
          horizontalMediaCardDetails: { image: '', title: '' },
          akomiMediaCard: [],
          horizontalMediaCardIndex: 0,
        });
      }
    }
  }, [searchKey, params.galleryId]);

  if (initialLoading || searchableCount === null) {
    // Show layout-appropriate skeleton during initial load
    if (layout === INLINE) {
      return (
        <Container maxWidth={false} data-testid="library-grid-skeleton">
          <ListView assetsData={[]} />
        </Container>
      );
    }

    return (
      <Container maxWidth={false} data-testid="library-grid-skeleton">
        <Grid container wrap="wrap" direction="row" alignItems="flex-start">
          {Array.from({ length: 12 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders have no stable id
            <Grid key={i} item {...mediaCardBreakPoints}>
              <Box display="flex" alignItems="flex-start" justifyContent="center" margin={densityConfig.cardMargin}>
                <SkeletonMediaCard />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (searchableCount > 0) {
    const leftW = viewState.showLeftSidebar ? viewState.leftDrawerWidth : 0;
    const rightW = viewState.showRightSidebar ? viewState.rightDrawerWidth : 0;
    const breakpointKey = widthToBreakPoint(windowWidth - leftW - rightW) as BreakpointKey;
    const itemsPerRow = 12 / (mediaCardBreakPoints[breakpointKey] as number);

    return (
      <>
        <CollectionPreviewModal
          collectionData={
            viewState.collectionData as unknown as Parameters<typeof CollectionPreviewModal>[0]['collectionData']
          }
        />

        <Container maxWidth={false} data-testid="library-grid-results">
          <div>
            {(layout === GRID || !layout) && (
              <Grid container={true} wrap="wrap" direction="row" alignItems="flex-start">
                {items.map((item, index) => {
                  const isLastInRow = (index + 1) % itemsPerRow === 0 || index === items.length - 1;

                  if (isLastInRow) {
                    return (
                      <React.Fragment key={item.id}>
                        <Grid item {...mediaCardBreakPoints}>
                          <Box
                            display="flex"
                            alignItems="flex-start"
                            justifyContent="center"
                            margin={densityConfig.cardMargin}
                          >
                            <AkomiMediaCardRouter index={index} item={item} />
                          </Box>
                        </Grid>
                        <MediaCardHorizontalDetails
                          mediaCardMediaPageIndex={index}
                          cardIndex={index}
                          mediaCardBreakPoints={mediaCardBreakPoints}
                          windowWidth={windowWidth}
                          akomiFileName={item.name || ''}
                          assetsData={items}
                          maxIndex={items.length - 1}
                        />
                      </React.Fragment>
                    );
                  }

                  return (
                    <Grid key={item.id} item {...mediaCardBreakPoints}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="center"
                        margin={densityConfig.cardMargin}
                      >
                        <AkomiMediaCardRouter item={item} index={index} />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {layout === INLINE && <ListView assetsData={items} />}
            {layout === TWOUP && (
              <TwoUpView assetsData={items} mediaCardBreakPoints={mediaCardBreakPoints} windowWidth={windowWidth} />
            )}
            {layout === GALLERY_ONLY && (
              <GalleryOnlyView
                assetsData={items}
                mediaCardBreakPoints={mediaCardBreakPoints}
                windowWidth={windowWidth}
              />
            )}
          </div>
          <HomePagination showRecordCount={true} />
          <Typography className={classes.totalCount} data-testid="library-total-count">
            Total Items: {trueTotalCount ?? 0}
          </Typography>

          <ActionDialogContext.Provider value={{ refetch }}>
            {viewState.globalAction === MANAGE_GALLERY_SLUG && (
              <CreateGallerySwitch
                isOpen={viewState.globalAction === MANAGE_GALLERY_SLUG}
                onClose={() => viewStateVar({ ...viewState, globalAction: null })}
                assetIds={viewState.selectedAssets}
              />
            )}
            {viewState.globalAction === ADD_TO_GALLERY_SLUG && (
              <UpdateGalleryDialog
                isOpen={viewState.globalAction === ADD_TO_GALLERY_SLUG}
                onClose={() => viewStateVar({ ...viewState, globalAction: null })}
                assetIds={viewState.selectedAssets}
              />
            )}
            {viewState.shareDialog ? ((<ShareDialog />) as unknown as React.ReactElement) : null}
            {viewState.libraryDownloadDialog
              ? ((
                  <LibraryDownloadDialog isOpen={!!viewState.libraryDownloadDialog} />
                ) as unknown as React.ReactElement)
              : null}
            {viewState.deleteDialog
              ? ((
                  <DeleteDialog refetchQ={buildSimpleOSQuery() as unknown as string} />
                ) as unknown as React.ReactElement)
              : null}
            {viewState.hiresDialog
              ? ((
                  <HiresDialog isOpen={!!viewState.hiresDialog} isSingleAssetOnMediaPage={false} />
                ) as unknown as React.ReactElement)
              : null}
            {viewState.globalAction === REMOVE_FROM_GALLERY_SLUG && (
              <RemoveGalleryItem
                isOpen={!!(viewState.globalAction === REMOVE_FROM_GALLERY_SLUG && params.galleryId)}
                onClose={() => viewStateVar({ ...viewState, globalAction: null })}
              />
            )}
          </ActionDialogContext.Provider>
        </Container>
      </>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="65vh"
      width="100%"
      data-testid="library-grid-empty"
    >
      <Typography component="h1" variant="h6">
        <strong>No results found</strong>
      </Typography>
    </Box>
  );
};

export default AkomiMediaCardGrid;
